import { Type } from "@sinclair/typebox";
import { isoTimestampSchema, opaqueIdSchema } from "./common.js";
export const providerPreviewExpiredProblemType = "https://new.roofseye.com/problems/provider-preview-expired";
const sha256Schema = Type.String({ pattern: "^[0-9a-f]{64}$" });
const providerMoneySchema = Type.Object({
    amount: Type.String({ pattern: "^-?(0|[1-9][0-9]*)(\\.[0-9]{1,4})?$" }),
    currency: Type.String({ pattern: "^[A-Z]{3}$" })
}, { additionalProperties: false });
export const providerProductKeySchema = Type.Union([
    Type.Literal("cadastral_units"),
    Type.Literal("cadastral_report"),
    Type.Literal("cadastral_person"),
    Type.Literal("cadastral_national"),
    Type.Literal("company_search"),
    Type.Literal("company_start"),
    Type.Literal("company_advanced"),
    Type.Literal("company_pec"),
    Type.Literal("company_marketing"),
    Type.Literal("geocode_address"),
    Type.Literal("reverse_geocode"),
    Type.Literal("map_search_session"),
    Type.Literal("map_isochrone")
]);
export const providerRequestStateSchema = Type.Union([
    Type.Literal("approved"),
    Type.Literal("submitting"),
    Type.Literal("provider_pending"),
    Type.Literal("reconciling"),
    Type.Literal("completed"),
    Type.Literal("input_rejected"),
    Type.Literal("credit_blocked"),
    Type.Literal("no_data"),
    Type.Literal("partial"),
    Type.Literal("retry_wait"),
    Type.Literal("outcome_uncertain"),
    Type.Literal("quarantined"),
    Type.Literal("failed"),
    Type.Literal("cancelled")
]);
export const providerPreviewSchema = Type.Object({
    id: opaqueIdSchema,
    providerKey: Type.String({ minLength: 1 }),
    productKey: Type.String({ minLength: 1 }),
    productLabel: Type.String({ minLength: 1 }),
    purpose: Type.String({ minLength: 1, maxLength: 300 }),
    subjectType: Type.String({ minLength: 1 }),
    subjectId: Type.String({ minLength: 1 }),
    reusableSnapshotId: Type.Optional(opaqueIdSchema),
    reusableSnapshotAgeSeconds: Type.Optional(Type.Integer({ minimum: 0 })),
    maximumCalls: Type.Integer({ minimum: 0 }),
    configuredUnitCost: providerMoneySchema,
    maximumTotal: providerMoneySchema,
    pricingCheckedAt: isoTimestampSchema,
    approvalToken: sha256Schema,
    expiresAt: isoTimestampSchema
}, { $id: "ProviderPreview", additionalProperties: false });
export const providerPreviewRequestSchema = Type.Object({
    product: Type.Union([
        Type.Literal("company_search"),
        Type.Literal("geocode_address"),
        Type.Literal("reverse_geocode"),
        Type.Literal("map_search_session"),
        Type.Literal("map_isochrone")
    ]),
    purpose: Type.String({ minLength: 2, maxLength: 300 }),
    subject: Type.Object({
        type: Type.Union([
            Type.Literal("building"),
            Type.Literal("organization"),
            Type.Literal("cadastral_reference"),
            Type.Literal("search_query"),
            Type.Literal("work_scope"),
            Type.Literal("work_bundle_preparation_item")
        ]),
        id: opaqueIdSchema
    }),
    parameters: Type.Record(Type.String(), Type.Unknown())
}, { $id: "ProviderPreviewRequest", additionalProperties: false });
export const approveProviderRequestSchema = Type.Object({
    previewId: opaqueIdSchema,
    approvalToken: sha256Schema
}, { $id: "ApproveProviderRequest", additionalProperties: false });
export const recoverGeocodingProviderRequestSchema = Type.Object({
    idempotencyKey: Type.String({ minLength: 8, maxLength: 200 }),
    product: Type.Literal("geocode_address"),
    purpose: Type.String({ minLength: 2, maxLength: 300 }),
    subject: Type.Object({
        type: Type.Literal("search_query"),
        id: opaqueIdSchema
    }, { additionalProperties: false }),
    parameters: Type.Object({ address: Type.String({ minLength: 3, maxLength: 500 }) }, { additionalProperties: false })
}, { $id: "RecoverGeocodingProviderRequest", additionalProperties: false });
export const providerGeocodingResultSchema = Type.Object({
    id: Type.String({ minLength: 1, maxLength: 200 }),
    coordinates: Type.Object({
        latitude: Type.Number({ minimum: -90, maximum: 90 }),
        longitude: Type.Number({ minimum: -180, maximum: 180 })
    }, { additionalProperties: false }),
    bounds: Type.Optional(Type.Object({
        south: Type.Number({ minimum: -90, maximum: 90 }),
        west: Type.Number({ minimum: -180, maximum: 180 }),
        north: Type.Number({ minimum: -90, maximum: 90 }),
        east: Type.Number({ minimum: -180, maximum: 180 })
    }, { additionalProperties: false })),
    label: Type.String({ minLength: 1, maxLength: 500 }),
    components: Type.Object({
        streetNumber: Type.Optional(Type.String({ minLength: 1, maxLength: 50 })),
        streetName: Type.Optional(Type.String({ minLength: 1, maxLength: 300 })),
        postalCode: Type.Optional(Type.String({ minLength: 1, maxLength: 30 })),
        locality: Type.Optional(Type.String({ minLength: 1, maxLength: 300 })),
        subLocality: Type.Optional(Type.String({ minLength: 1, maxLength: 300 })),
        adminLevels: Type.Optional(Type.Array(Type.Object({
            level: Type.Integer({ minimum: 0, maximum: 20 }),
            name: Type.Optional(Type.String({ minLength: 1, maxLength: 300 })),
            code: Type.Optional(Type.String({ minLength: 1, maxLength: 100 }))
        }, { additionalProperties: false }), { maxItems: 10 })),
        country: Type.Optional(Type.String({ minLength: 1, maxLength: 100 })),
        countryCode: Type.Optional(Type.String({ pattern: "^[A-Z]{2}$" })),
        timezone: Type.Optional(Type.String({ minLength: 1, maxLength: 100 }))
    }, { additionalProperties: false }),
    providedBy: Type.Optional(Type.String({ minLength: 1, maxLength: 100 }))
}, { $id: "ProviderGeocodingResult", additionalProperties: false });
export const providerResultSummarySchema = Type.Object({ geocodingResult: Type.Optional(providerGeocodingResultSchema) }, { additionalProperties: true });
export const providerResultFreshnessSchema = Type.Object({
    observedAt: isoTimestampSchema,
    expiresAt: isoTimestampSchema,
    stale: Type.Boolean()
}, { $id: "ProviderResultFreshness", additionalProperties: false });
export const providerRequestSchema = Type.Object({
    id: opaqueIdSchema,
    state: providerRequestStateSchema,
    providerKey: Type.String(),
    productKey: Type.String(),
    productLabel: Type.String(),
    purpose: Type.String(),
    configuredMaximumCost: providerMoneySchema,
    actualCost: Type.Optional(providerMoneySchema),
    resultSummary: Type.Optional(providerResultSummarySchema),
    resultFreshness: Type.Optional(providerResultFreshnessSchema),
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema
}, { $id: "ProviderRequest", additionalProperties: false });
export const recoverGeocodingProviderResponseSchema = Type.Object({
    request: Type.Union([providerRequestSchema, Type.Null()])
}, { $id: "RecoverGeocodingProviderResponse", additionalProperties: false });
//# sourceMappingURL=providers.js.map